import { Button, Input, Dropdown, Pagination, Tag, Collapse } from 'antd';

import { useAppDispatch, useAppSelector } from '@hooks/redux';

import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd/lib';
import getStatus from '@utils/getStatus';
import { useEffect, useState } from 'react';
import { deleteCounterpartyApi, downloadCounterpartyListApi, getAllCounterpartiesApi } from '@http/counterparties';
import CounterpartyInfo from './CounterpartyInfo';
import { activePanelAC } from '@store/reducers/counterparties';
import {
  repeatInterbankTransactionAC,
  repeatInternalTransactionAC,
  repeatSwiftRubTransactionAC,
  repeatSwiftTransactionAC,
} from '@store/reducers/transactions';

const { Panel } = Collapse;

function Counterparties() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const counterpartiesList = useAppSelector(state => state.counterpartiesReducer.counterpartiesList);
  const activePanel = useAppSelector(state => state.counterpartiesReducer.activePanel);

  const permissions = useAppSelector(state => state.userReducer.permissions);
  const [currentPage, setCurrentPage] = useState(1);

  const openPanel = (key: any) => {
    dispatch(activePanelAC(key));
  };

  const changePage = (page: number) => {
    dispatch(activePanelAC(''));
    setCurrentPage(page);

    getAllCounterparties(page);
  };

  const getAllCounterparties = (page: number) => {
    dispatch(getAllCounterpartiesApi(page, {}, 10));
  };

  const deleteCounterparty = (item: any) => {
    dispatch(deleteCounterpartyApi(item.id)).then(data => {
      if (data) {
        dispatch(getAllCounterpartiesApi(currentPage, {}, 10));
        dispatch(activePanelAC(''));
      }
    });
  };

  const repeatTransactionHandler = (item: any, type: string) => {
    switch (type) {
      case 'INTERNAL':
        dispatch(repeatInternalTransactionAC(item));
        break;
      case 'INTERBANK':
        dispatch(repeatInterbankTransactionAC(item));
        break;
      case 'SWIFT':
        dispatch(repeatSwiftTransactionAC(item));
        break;
      case 'SWIFT_RUB':
        dispatch(repeatSwiftRubTransactionAC(item));
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    dispatch(getAllCounterpartiesApi(1, {}, 10));
  }, []);

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[1130px] w-full'>
        <div className='flex'>
          <div className='p-4 lg:p-8 w-full h-full bg-white mb-12'>
            <div>
              <div className='flex justify-between items-center'>
                <h1 className='max-md:hidden text-2xl mb-2 font-semibold color-active'>{t('Counterparties')}</h1>
                <div className='flex gap-3'>
                  {permissions?.cpty[0]?.allow_view ? (
                    <Button
                      type='primary'
                      onClick={() => {
                        dispatch(downloadCounterpartyListApi());
                      }}
                    >
                      {t('Download list')}
                    </Button>
                  ) : null}
                  {permissions?.cpty[0]?.allow_init ? (
                    <Button
                      type='primary'
                      onClick={() => {
                        navigate('/counterparties/create');
                      }}
                    >
                      {t('Add')}
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className='mt-7'>
                <div className=''>
                  <div className='p-5 grid grid-cols-12 ml-10 gap-5 font-semibold text-sm color-active'>
                    <p className='col-span-1'>{t('Date')}</p>
                    <p className='col-span-3'>{t('Name')}</p>
                    <p className='col-span-3'>{t('Account')}</p>
                    <p className='col-span-1'>{t('TIN')}</p>
                    <p className='col-span-1 text-right'>{t('Resident')}</p>
                    <p className='col-span-2 text-right'>{t('Status')}</p>
                  </div>
                </div>
                {counterpartiesList?.results?.length > 0 ? (
                  <div className='pt-0'>
                    <Collapse activeKey={activePanel} onChange={openPanel} ghost className='p-0'>
                      {counterpartiesList?.results?.map((item: any) => {
                        const menuItems: MenuProps['items'] = [];
                        const deleteMenuItems: MenuProps['items'] = [];

                        if (item.counterparty_type === 'RESIDENT') {
                          menuItems.push({
                            key: 'internal',
                            label: <p>{t('Internal')}</p>,
                            onClick: () => {
                              navigate('/payments/internal');
                              repeatTransactionHandler(item, 'INTERNAL');
                            },
                          });
                          menuItems.push({
                            key: 'interbank',
                            label: <p>{t('Interbank')}</p>,
                            onClick: () => {
                              navigate('/payments/interbank');
                              repeatTransactionHandler(item, 'INTERBANK');
                            },
                          });
                        } else {
                          menuItems.push({
                            key: 'swift',
                            label: <p>{t('Swift')}</p>,
                            onClick: () => {
                              navigate('/payments/swift');
                              repeatTransactionHandler(item, 'SWIFT');
                            },
                          });
                          menuItems.push({
                            key: 'swift-ru',
                            label: <p>{t('Ruble')}</p>,
                            onClick: () => {
                              navigate('/payments/swift_rub');
                              repeatTransactionHandler(item, 'SWIFT_RUB');
                            },
                          });
                        }

                        deleteMenuItems.push({
                          key: 'delete',
                          label: <p>Удалить</p>,
                          danger: true,
                          onClick: () => deleteCounterparty(item),
                        });

                        return (
                          <Panel
                            header={
                              <div
                                key={item.id}
                                className='p-5 grid grid-cols-12 gap-5 listItem items-center text-sm'
                                style={{ borderBottom: '1px solid #eee' }}
                              >
                                <p className='col-span-1 text-center'>{item.created_at.slice(0, 10)}</p>
                                <p className='col-span-3 font-semibold'>{item.title}</p>
                                <p className='col-span-3'>{item.receiving_ac}</p>
                                <p className='col-span-1'>{item.tax_id}</p>
                                <p className='col-span-1 text-right'>
                                  {item.counterparty_type === 'RESIDENT' ? (
                                    <Tag color='#87d068'>{t('Yes')}</Tag>
                                  ) : (
                                    <Tag color='#108ee9'>{t('No')}</Tag>
                                  )}
                                </p>
                                <p className='col-span-2 text-right'>{getStatus(item.status, 12, t)}</p>

                                <div
                                  onClick={e => {
                                    e.stopPropagation();
                                  }}
                                  className='flex justify-end items-center gap-3'
                                >
                                  <Dropdown menu={{ items: deleteMenuItems }}>
                                    <DeleteOutlined style={{ fontSize: '20px' }} />
                                  </Dropdown>
                                  <div className='w-6'>
                                    {item.status !== 'IN_APPROVAL' && item.status !== 'DECLINED' ? (
                                      <Dropdown menu={{ items: menuItems }}>
                                        <MenuOutlined style={{ fontSize: '20px' }} className='cursor-pointer' />
                                      </Dropdown>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            }
                            key={item.trans_id}
                          >
                            <CounterpartyInfo counterpartyInfo={item} />
                          </Panel>
                        );
                      })}
                    </Collapse>
                    <div className='mt-7 flex justify-end'>
                      <Pagination
                        onChange={changePage}
                        current={counterpartiesList.current_page}
                        defaultPageSize={10}
                        total={counterpartiesList.total_pages * 10}
                        showSizeChanger={false}
                      />
                    </div>
                  </div>
                ) : (
                  <div className='h-24 lg:h-72 flex items-center justify-center'>
                    <p>{t('No Transactions')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Counterparties;
